import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { operationType, selectDataType } from '../../../../../services/types';
import { Loader, Main, Styled } from './style';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';
import { AuthenticateSelect } from '../../../../auth/select';
import { TextInput } from '../../../../form/textInput';
import { Switch } from '../../../../form/switch';
import { Select } from '../../../../form/select';
import { updateNodeValue } from '../../../../../services/method';

type ImapTriggerProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

export function ImapTrigger({ id, data, setChildMinimize, setChildSettings }: ImapTriggerProps) {
    const { setNodes } = useReactFlow();
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);

    // Mailbox options
    const [mailboxOptions, setMailboxOptions] = React.useState<selectDataType[]>([
        { id: 1, name: 'INBOX', refId: 'INBOX', isActive: true }
    ]);

    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // Set defaults
    React.useEffect(() => {
        const updates: Record<string, unknown> = {};
        if (!data.value || data.value.fetchUnseenOnly === undefined) updates.fetchUnseenOnly = true;
        if (!data.value || data.value.markAsSeen === undefined) updates.markAsSeen = false;
        if (!data.value || data.value.limit === undefined) updates.limit = 10;
        if (!data.value || data.value.mailbox === undefined) updates.mailbox = 'INBOX';
        if (!data.value || data.value.isActive === undefined) updates.isActive = false;
        if (Object.keys(updates).length) updateNodeValue(id, setNodes, updates);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAuthenticate = (type: 'add' | 'remove', authData?: any) => {
        if (type === 'add' && authData) {
            updateNodeValue(id, setNodes, { connection: authData });
        } else {
            updateNodeValue(id, setNodes, { connection: null });
        }
    };

    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string) => {
        if (!keyRef) return;
        const raw = e.target.value;
        const value = e.target.type === 'number' ? (raw === '' ? '' : Number(raw)) : raw;
        updateNodeValue(id, setNodes, { [keyRef]: value });
    };

    const handleSelectMailbox = (selectId: number) => {
        setMailboxOptions(ops => {
            const updated = ops.map(op => ({ ...op, isActive: op.id === selectId }));
            const selected = updated.find(op => op.id === selectId);
            if (selected?.refId) {
                updateNodeValue(id, setNodes, { mailbox: selected.refId });
            }
            return updated;
        });
    };

    const isWarning = !data.value?.connection;

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}

            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={isWarning}
                style={{ width: '420px' }}
            >
                <Styled>
                    {/* Connection Selection */}
                    <AuthenticateSelect
                        type="imap"
                        defaultId={data.value?.connection?.id}
                        handleAuthenticate={handleAuthenticate}
                        background={data.background}
                    />

                    <div className="scroll">
                        {/* Email Filters Section */}
                        <div className="section-title">Email Filters</div>
                        <TextInput
                            keyRef="subjectFilter"
                            title="Subject Filter (optional)"
                            placeholder="e.g., 'Order Confirmation'"
                            value={data.value?.subjectFilter || ''}
                            handleChange={handleChangeInput}
                        />
                        <TextInput
                            keyRef="fromFilter"
                            title="From Filter (optional)"
                            placeholder="e.g., 'noreply@example.com'"
                            value={data.value?.fromFilter || ''}
                            handleChange={handleChangeInput}
                        />
                        {/* Processing Options Section */}
                        <div className="section-title">Processing Options</div>
                        <TextInput
                            keyRef="limit"
                            title="Max emails per check"
                            type="number"
                            min={1}
                            max={50}
                            placeholder="10"
                            value={data.value?.limit === undefined ? '' : String(data.value.limit)}
                            handleChange={handleChangeInput}
                        />
                    </div>
                    {/* Polling Section */}
                    <div className="section-title">Automation</div>

                    <Switch
                        title="Enable Polling (check every minute)"
                        value={!!data.value?.isActive}
                        setValue={() => updateNodeValue(id, setNodes, { isActive: !data.value?.isActive })}
                    />

                    <p className="note">
                        When polling is enabled, the trigger automatically checks for
                        new emails every minute and starts the workflow when matching
                        emails are found.
                    </p>
                </Styled>
            </ExpandedBlock> : null}
        </Main>
    </>);
}